/**
 * Resolves whichever row in `list` currently holds focus, so a list-level keyboard shortcut can
 * act on it. Returns `undefined` whenever something other than a row owns the keyboard.
 */
export function focusedRow(list: HTMLElement | undefined): HTMLLIElement | undefined {
  const activeElement = document.activeElement
  if (!list || !(activeElement instanceof HTMLElement)) return

  // Scoped commands intentionally still fire while focus is inside an overlay, because
  // menus and dialogs render through portals that remain in the React tree. Those
  // portals are not DOM descendants of the list, so requiring containment is what keeps
  // the shortcut from hijacking the key from, say, the linked pull request menu.
  if (!list.contains(activeElement)) return

  const row = activeElement.closest('li')
  if (!row || !list.contains(row)) return

  // Containment cannot see an overlay that keeps focus within the row — a menu whose
  // trigger stays focused, or an inline rather than portalled overlay. An expanded
  // control between the focused element and the row covers both. The row itself is
  // excluded because `ListItem` uses `aria-expanded` for nested lists, which does not
  // take over the keyboard.
  const expandedControl = activeElement.closest('[aria-expanded="true"]')
  if (expandedControl && expandedControl !== row) return

  return row
}

/**
 * Toggles the selection of whichever row in `list` currently holds focus, which is the state
 * clicking that row's checkbox reaches. Returns without acting when the list is not selectable,
 * since `ListItemSelection` renders nothing in that case.
 */
export function toggleFocusedRowSelection(list: HTMLElement | undefined) {
  // `data-listview-component` is the production-stable hook for the row's checkbox; `data-testid`
  // is stripped in production. Clicking it rather than calling the row's `onSelect` keeps the
  // `aria-disabled` check in `ListItemSelection` in play, so a disabled selection stays disabled.
  focusedRow(list)?.querySelector<HTMLInputElement>('[data-listview-component="selection-input"]')?.click()
}

/**
 * Activates the primary link of whichever row in `list` currently holds focus, which is
 * the destination clicking the row reaches. Deliberately activates the row even when
 * focus sits on a nested control, so the `o` shortcut never triggers that control
 * instead. Returns without acting whenever something other than the row owns the
 * keyboard.
 */
export function activateFocusedRow(list: HTMLElement | undefined) {
  // The title container is the only production-stable hook for the row's primary link
  // (`data-testid` is stripped in production). The heading precedes the trailing badges
  // inside it, so the first anchor is the title link rather than a label link.
  focusedRow(list)?.querySelector<HTMLAnchorElement>('[data-listview-item-title-container] a[href]')?.click()
}
